import { useMutation, type UseMutationOptions, useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { ApiClient } from './api.client.js';
import { type Code, type CodeMetadata, type CreateCode, type CreateCodeMetadata, type UpdateCode, type UpdateCodeMetadata } from '../models/index.js';

export const CodesApiKeys = {
  GET_CODE: 'GET_CODE',
  GET_CODES: 'GET_CODES',
  CREATE_CODE: 'CREATE_CODE',
  UPDATE_CODE: 'UPDATE_CODE',
  DELETE_CODE: 'DELETE_CODE',
  CREATE_CODE_METADATA: 'CREATE_CODE_METADATA',
  UPDATE_CODE_METADATA: 'UPDATE_CODE_METADATA',
  DELETE_CODE_METADATA: 'DELETE_CODE_METADATA',
};

export interface CodesQueryParams {
  includeMetadata?: boolean;
  includeCategory?: boolean;
}

export class CodesApi extends ApiClient {
  public static async getCode(id: string, options: CodesQueryParams): Promise<Code> {
    return this.http.get(`/codes/${id}`, { params: options }).then((res) => res.data);
  }
  public static async getAllCodes(options: CodesQueryParams): Promise<Array<Code>> {
    return this.http
      .get(`/codes`, {
        params: {
          ...(options.includeCategory && { includeCategory: true }),
          ...(options.includeMetadata && { includeMetadata: true }),
        },
      })
      .then((res) => res.data);
  }
  public static async createCode(code: CreateCode): Promise<Code> {
    return this.http.post('/codes', code);
  }
  public static async updateCode(code: UpdateCode): Promise<Code> {
    return this.http.put(`/codes/${code.id}`, code);
  }
  public static async deleteCode(codeId: string): Promise<void> {
    return this.http.delete(`/codes/${codeId}`);
  }
  public static async createCodeMetaData(codeMetadata: CreateCodeMetadata): Promise<CodeMetadata> {
    return this.http.post(`/code-metadata`, codeMetadata);
  }
  public static async updateCodeMetaData(codeMetadata: UpdateCodeMetadata): Promise<CodeMetadata> {
    return this.http.put(`/code-metadata/${codeMetadata.id}`, codeMetadata);
  }
  public static async deleteCodeMetaData(codeMetadataId: string): Promise<void> {
    return this.http.delete(`/code-metadata/${codeMetadataId}`);
  }
}

export function useCode(id: string, options: CodesQueryParams, queryOptions?: UseQueryOptions<Code>) {
  return useQuery<Code>([CodesApiKeys.GET_CODE, id], () => CodesApi.getCode(id, options), {
    cacheTime: 0,
    ...queryOptions,
  });
}

export function useAllCodes(options: CodesQueryParams, queryOptions?: UseQueryOptions<Code[]>) {
  return useQuery<Code[]>([CodesApiKeys.GET_CODES], () => CodesApi.getAllCodes(options), queryOptions);
}

export function useMutateCreateCode(mutationOptions?: UseMutationOptions<Code, unknown, CreateCode>) {
  return useMutation([CodesApiKeys.CREATE_CODE], (code: CreateCode) => CodesApi.createCode(code), mutationOptions);
}

export function useMutateUpdateCode(mutationOptions?: UseMutationOptions<Code, unknown, UpdateCode>) {
  return useMutation([CodesApiKeys.UPDATE_CODE], (code: UpdateCode) => CodesApi.updateCode(code), mutationOptions);
}

export function useMutateDeleteCode(mutationOptions?: UseMutationOptions<void, unknown, string>) {
  return useMutation([CodesApiKeys.DELETE_CODE], (codeId: string) => CodesApi.deleteCode(codeId), mutationOptions);
}
export function useMutateCreateCodeMetaData(mutationOptions?: UseMutationOptions<CodeMetadata, unknown, CreateCodeMetadata>) {
  return useMutation([CodesApiKeys.CREATE_CODE_METADATA], (codeMetadata: CreateCodeMetadata) => CodesApi.createCodeMetaData(codeMetadata), mutationOptions);
}
export function useMutateUpdateCodeMetaData(mutationOptions?: UseMutationOptions<CodeMetadata, unknown, UpdateCodeMetadata>) {
  return useMutation([CodesApiKeys.UPDATE_CODE_METADATA], (codeMetadata: UpdateCodeMetadata) => CodesApi.updateCodeMetaData(codeMetadata), mutationOptions);
}
export function useMutateDeleteCodeMetaData(mutationOptions?: UseMutationOptions<void, unknown, string>) {
  return useMutation([CodesApiKeys.DELETE_CODE_METADATA], (codeMetadataId: string) => CodesApi.deleteCodeMetaData(codeMetadataId), mutationOptions);
}
